import { REVIEW_MIN_INTERVAL_MS } from '../constants';

/** Повод, по которому человек заработал право быть спрошенным. Идёт в аналитику как есть. */
export type ReviewOutcomeReason = 'chat' | 'session';

/** Заработанное право: когда и за что. */
export type EarnedOutcome = {
  at: number;
  reason: ReviewOutcomeReason;
};

const REASONS = new Set<string>(['chat', 'session']);

/** Всё, от чего зависит решение. Собирается вызывающим, чтобы сама проверка осталась чистой. */
export type ReviewGateInput = {
  now: number;
  /** Когда человек получил от продукта результат; `null` — не получал. */
  earnedAt: number | null;
  /** Начало текущего захода: холодный старт или возвращение из фона. */
  visitStartedAt: number;
  /** Отметка о прошлой попытке; `null` — не просили ни разу. */
  lastAskedAt: number | null;
  /** Рубильник из `/app/status.json`. */
  enabled: boolean;
  /** Экран уже занят другим разговором с человеком. */
  busy: boolean;
};

/**
 * Можно ли просить оценку прямо сейчас.
 *
 * Два правила поверх обычных ограничителей. Первое: спрашиваем только у того, кто уже получил
 * от продукта результат, — правило Play гласит «после того, как человек испытал приложение
 * достаточно, чтобы судить», а создание заявки это надежда, а не результат. Второе: результат
 * должен быть из прошлого захода. Просьба, догоняющая человека в том же заходе, всё равно
 * ложится поверх его дела, — а отложенная приходит на пустой экран и ничего не перебивает.
 */
export const canAskForReview = ({
  now,
  earnedAt,
  visitStartedAt,
  lastAskedAt,
  enabled,
  busy,
}: ReviewGateInput): boolean => {
  if (!enabled || busy) {
    return false;
  }

  if (earnedAt === null || !Number.isFinite(earnedAt)) {
    return false;
  }

  // Право, заработанное в этом же заходе, ждёт следующего. Сюда же попадает отметка «из
  // будущего» при уехавших часах: лишний заход ожидания дешевле, чем просьба поверх дела.
  if (earnedAt >= visitStartedAt) {
    return false;
  }

  if (lastAskedAt === null || !Number.isFinite(lastAskedAt)) {
    return true;
  }

  // Отметка из будущего — это уехавшие часы телефона, а не запрет. Считать её действующей
  // значило бы выключить карточку навсегда одним переводом времени назад.
  if (lastAskedAt > now) {
    return true;
  }

  return now - lastAskedAt >= REVIEW_MIN_INTERVAL_MS;
};

/** Разбор отметки из хранилища. Мусор трактуется как «не просили» — см. `canAskForReview`. */
export const parseLastAskedAt = (raw: string | null): number | null => {
  if (!raw) {
    return null;
  }

  const parsed = Number.parseInt(raw, 10);

  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Разбор заработанного права. Всё, что не разобралось, — «права нет»: лучше не спросить
 * вовсе, чем спросить по поводу, который потом не с чем будет сопоставить в аналитике.
 */
export const parseEarnedOutcome = (raw: string | null): EarnedOutcome | null => {
  if (!raw) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(raw);

    if (!parsed || typeof parsed !== 'object') {
      return null;
    }

    const { at, reason } = parsed as { at?: unknown; reason?: unknown };

    if (typeof at !== 'number' || !Number.isFinite(at)) {
      return null;
    }

    if (typeof reason !== 'string' || !REASONS.has(reason)) {
      return null;
    }

    return { at, reason: reason as ReviewOutcomeReason };
  } catch {
    return null;
  }
};
